import { err, ok, Result } from 'neverthrow';
import { FunctionTypingRule } from '../function-call';
import { TypeCheckError } from '../../type-check-error';
import { Ty } from '../../../ty';
import { Assert } from '../../../utils';
import _ from 'lodash';
import { Errs } from '../../type-checker';
import { TC } from '../..';

export const categoryConcatTypingRule: FunctionTypingRule = (
  _op,
  args,
  _argAsts
) =>
  Result.combine(args.map((arg) => reqEnum(arg)))
    .map((enumTys) => crossProduct(enumTys))
    .map((t) => {
      const output: Ty.ExtendedAttributeType = {
        ty: { k: 'enum', t: _.uniq(t).sort() },
        nullable: args.some((arg) => arg.nullable),
        tags: [],
      };
      return output;
    });

export const categoryAtTypingRule: FunctionTypingRule = (_op, args) => {
  const [target, pos, ...rest] = args;
  Assert.assert(
    target !== undefined && pos !== undefined && rest.length === 0,
    'Arity already checked'
  );

  const nullable = args.some((arg) => arg.nullable);

  const posRes = reqRange(pos);

  if (posRes.isErr()) {
    return err(posRes.error);
  }

  const targetRes = reqEnum(target);

  if (targetRes.isErr()) {
    return TC.implementsTy({ subject: target, req: 'string' })
      ? ok({
          ty: { k: 'primitive', t: 'string' },
          nullable,
          tags: [],
        })
      : err(targetRes.error);
  }

  const { start, end } = posRes.value;
  const t: string[] = [];

  for (const variant of targetRes.value) {
    const parts = Ty.splitEnumVariant(variant);
    for (let i = start - 1; i <= Math.min(end - 1, parts.length); i++) {
      t.push(parts[i] ?? 'N/A');
    }
  }

  const res: Ty.ExtendedAttributeType = {
    ty: { k: 'enum', t: _.uniq(t).sort() },
    nullable,
    tags: [],
  };

  return ok(res);
};

const reqRange = (
  ty: Ty.ExtendedAttributeType
): Result<{ start: number; end: number }, TypeCheckError> => {
  if (ty.ty.k !== 'range') {
    return err(new Errs.RangeTypeRequired({ got: ty }));
  }
  return ok(ty.ty);
};

const reqEnum = (
  arg: Ty.ExtendedAttributeType
): Result<readonly string[], TypeCheckError> => {
  if (arg.ty.k !== 'enum') {
    return err(new Errs.EnumTypeRequired({ got: arg }));
  }

  return ok(arg.ty.t);
};

const crossProduct = (variants: (readonly string[])[]): string[] => {
  const result: string[] = [];

  const generateCombinations = (prefix: string, index: number): void => {
    if (index === variants.length) {
      if (prefix !== '') {
        result.push(prefix);
      }
      return;
    }

    for (let i = 0; i < variants[index]!.length; i++) {
      const next = variants[index]![i];
      if (next === Ty.NOT_APPLICABLE) {
        result.push(prefix === '' ? Ty.NOT_APPLICABLE : prefix);
      } else {
        const nextPrefix = `${prefix}${
          prefix !== '' ? Ty.ENUM_PATH_SEPERATOR : ''
        }${next}`;

        generateCombinations(nextPrefix, index + 1);
      }
    }
  };

  generateCombinations('', 0);
  return result;
};
